
import React, { useState, useEffect } from 'react'
import { 
  TrendingUp, 
  TrendingDown, 
  Users, 
  Building2, 
  DollarSign, 
  CreditCard, 
  AlertTriangle, 
  CheckCircle,
  Clock,
  Target,
  Activity,
  Calendar,
  FileText,
  Briefcase,
  UserCheck,
  Wrench,
  ShoppingCart,
  PieChart,
  BarChart3,
  ArrowUp,
  ArrowDown,
  PlayCircle,
  PauseCircle,
  CheckCircle2,
  AlertCircle
} from 'lucide-react'
import { useLeads } from '../hooks/useLeads'
import { useFinanceiro } from '../hooks/useFinanceiro'
import { usePlanejamentoObras } from '../hooks/usePlanejamentoObras'
import { useOrcamentos } from '../hooks/useOrcamentos'
import { useContratos } from '../hooks/useContratos'
import { useCompras } from '../hooks/useCompras'
import { useInsumos } from '../hooks/useInsumos'
import { useFuncionarios } from '../hooks/useFuncionarios'

interface KPICard {
  title: string
  value: string | number
  change: number
  changeType: 'increase' | 'decrease'
  icon: React.ReactNode
  color: string
  subtitle?: string
}

interface Alert {
  id: string
  type: 'warning' | 'error' | 'info' | 'success'
  title: string
  message: string
  priority: 'high' | 'medium' | 'low'
  module: string
  createdAt: string
}

interface AtividadeResumo {
  pendentes: number
  emAndamento: number
  concluidas: number
  atrasadas: number
  totalAtividades: number
  progressoMedio: number
}

const DashboardGeral: React.FC = () => {
  const { leads, metrics: leadsMetrics, loading: leadsLoading } = useLeads()
  const { metricas: financeiroMetrics, loading: financeiroLoading } = useFinanceiro()
  const { obras, atividades, metrics: obrasMetrics, loading: obrasLoading } = usePlanejamentoObras()
  const { orcamentos, metrics: orcamentosMetrics, loading: orcamentosLoading } = useOrcamentos()
  const { contratos, metrics: contratosMetrics, loading: contratosLoading } = useContratos()
  const { compras, metrics: comprasMetrics, loading: comprasLoading } = useCompras()
  const { insumos, metrics: insumosMetrics, loading: insumosLoading } = useInsumos()
  const { funcionarios, metrics: funcionariosMetrics, loading: funcionariosLoading } = useFuncionarios()

  const [alerts, setAlerts] = useState<Alert[]>([])
  const [selectedPeriod, setSelectedPeriod] = useState<'7d' | '30d' | '90d'>('30d')
  const [atividadesResumo, setAtividadesResumo] = useState<AtividadeResumo>({
    pendentes: 0,
    emAndamento: 0,
    concluidas: 0,
    atrasadas: 0,
    totalAtividades: 0,
    progressoMedio: 0
  })

  // 🔥 FUNÇÃO HELPER: Formatação segura de valores monetários
  const formatCurrency = (value: number | undefined | null): string => {
    const safeValue = Number(value) || 0
    return `R$ ${safeValue.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}`
  }

  // 🔥 FUNÇÃO HELPER: Formatação segura de números
  const formatNumber = (value: number | undefined | null): string => {
    const safeValue = Number(value) || 0
    return safeValue.toLocaleString('pt-BR')
  }

  // 🔥 FUNÇÃO HELPER: Cálculo seguro de percentual
  const calculatePercentage = (current: number | undefined | null, previous: number | undefined | null): number => {
    const safeCurrent = Number(current) || 0
    const safePrevious = Number(previous) || 0
    
    if (safePrevious === 0) return 0
    return ((safeCurrent - safePrevious) / safePrevious) * 100
  }

  // 🔥 NOVA FUNÇÃO: Calcular resumo das atividades
  const calculateAtividadesResumo = () => {
    if (!atividades || !Array.isArray(atividades)) {
      return {
        pendentes: 0,
        emAndamento: 0,
        concluidas: 0,
        atrasadas: 0,
        totalAtividades: 0,
        progressoMedio: 0
      }
    }

    const hoje = new Date()
    
    const pendentes = atividades.filter(atividade => atividade?.status === 'pendente').length
    const emAndamento = atividades.filter(atividade => atividade?.status === 'em_andamento').length
    const concluidas = atividades.filter(atividade => atividade?.status === 'concluida').length
    
    // Atividades atrasadas: status não é concluída e prazo vencido
    const atrasadas = atividades.filter(atividade => {
      if (!atividade?.prazo || atividade?.status === 'concluida') return false
      const dataPrazo = new Date(atividade.prazo)
      return dataPrazo < hoje && atividade.status !== 'concluida'
    }).length

    const totalAtividades = atividades.length
    
    // Calcular progresso médio das atividades
    const progressoMedio = totalAtividades > 0 
      ? atividades.reduce((acc, atividade) => acc + (Number(atividade?.progresso) || 0), 0) / totalAtividades 
      : 0

    return {
      pendentes,
      emAndamento,
      concluidas,
      atrasadas,
      totalAtividades,
      progressoMedio
    }
  }

  // Calcular KPIs principais com validações defensivas
  const calculateKPIs = (): KPICard[] => {
    const totalReceitas = Number(financeiroMetrics?.receitasMes) || 0
    const totalDespesas = Number(financeiroMetrics?.despesasMes) || 0
    const margemLucro = Number(financeiroMetrics?.margemLucro) || 0
    const saldoAtual = Number(financeiroMetrics?.saldoAtual) || 0

    // Comparativo mês anterior com validações
    const receitasAnterior = Number(financeiroMetrics?.comparativoMesAnterior?.receitas) || 0
    const despesasAnterior = Number(financeiroMetrics?.comparativoMesAnterior?.despesas) || 0
    const saldoAnterior = Number(financeiroMetrics?.comparativoMesAnterior?.saldo) || 0

    const changeReceitas = calculatePercentage(totalReceitas, receitasAnterior)
    const changeDespesas = calculatePercentage(totalDespesas, despesasAnterior)
    const changeSaldo = calculatePercentage(saldoAtual, saldoAnterior)

    return [
      {
        title: 'Receitas do Mês',
        value: formatCurrency(totalReceitas),
        change: changeReceitas,
        changeType: changeReceitas >= 0 ? 'increase' : 'decrease',
        icon: <TrendingUp className="w-6 h-6" />,
        color: 'bg-green-500',
        subtitle: `vs. mês anterior`
      },
      {
        title: 'Despesas do Mês',
        value: formatCurrency(totalDespesas),
        change: Math.abs(changeDespesas),
        changeType: changeDespesas <= 0 ? 'increase' : 'decrease',
        icon: <TrendingDown className="w-6 h-6" />,
        color: 'bg-red-500',
        subtitle: `vs. mês anterior`
      },
      {
        title: 'Saldo Atual',
        value: formatCurrency(saldoAtual),
        change: Math.abs(changeSaldo),
        changeType: changeSaldo >= 0 ? 'increase' : 'decrease',
        icon: <DollarSign className="w-6 h-6" />,
        color: saldoAtual >= 0 ? 'bg-blue-500' : 'bg-orange-500',
        subtitle: `vs. mês anterior`
      },
      {
        title: 'Margem de Lucro',
        value: `${margemLucro.toFixed(1)}%`,
        change: Math.abs(margemLucro - 20), // Meta de 20%
        changeType: margemLucro >= 20 ? 'increase' : 'decrease',
        icon: <Target className="w-6 h-6" />,
        color: margemLucro >= 20 ? 'bg-green-500' : 'bg-yellow-500',
        subtitle: `meta: 20%`
      }
    ]
  }

  // Gerar alertas do sistema com validações defensivas
  const generateAlerts = () => {
    const newAlerts: Alert[] = []

    // Validações defensivas para métricas financeiras
    const contasPagar = Number(financeiroMetrics?.contasPagar) || 0
    const saldoAtual = Number(financeiroMetrics?.saldoAtual) || 0
    const margemLucro = Number(financeiroMetrics?.margemLucro) || 0

    // Alertas financeiros
    if (contasPagar > saldoAtual && contasPagar > 0) {
      newAlerts.push({
        id: 'fluxo-caixa',
        type: 'error',
        title: 'Fluxo de Caixa Crítico',
        message: `Contas a pagar (${formatCurrency(contasPagar)}) excedem o saldo atual`,
        priority: 'high',
        module: 'Financeiro',
        createdAt: new Date().toISOString()
      })
    }

    if (margemLucro < 10 && margemLucro > 0) {
      newAlerts.push({
        id: 'margem-baixa',
        type: 'warning',
        title: 'Margem de Lucro Baixa',
        message: `Margem atual de ${margemLucro.toFixed(1)}% está abaixo do recomendado`,
        priority: 'medium',
        module: 'Financeiro',
        createdAt: new Date().toISOString()
      })
    }

    // Alertas de obras com validação
    if (obras && Array.isArray(obras)) {
      const obrasAtrasadas = obras.filter(obra => {
        if (!obra?.dataTerminoPrevisto) return false
        const dataTermino = new Date(obra.dataTerminoPrevisto)
        const hoje = new Date()
        return dataTermino < hoje && obra.status !== 'concluida'
      })

      if (obrasAtrasadas.length > 0) {
        newAlerts.push({
          id: 'obras-atrasadas',
          type: 'error',
          title: 'Obras Atrasadas',
          message: `${obrasAtrasadas.length} obra(s) com prazo vencido`,
          priority: 'high',
          module: 'Planejamento',
          createdAt: new Date().toISOString()
        })
      }
    }

    // Alertas de atividades atrasadas
    if (atividadesResumo.atrasadas > 0) {
      newAlerts.push({
        id: 'atividades-atrasadas',
        type: 'warning',
        title: 'Atividades Atrasadas',
        message: `${atividadesResumo.atrasadas} atividade(s) com prazo vencido`,
        priority: 'medium',
        module: 'Atividades',
        createdAt: new Date().toISOString()
      })
    }

    // Alertas de estoque com validação
    if (insumos && Array.isArray(insumos)) {
      const insumosEstoqueBaixo = insumos.filter(insumo => 
        insumo?.controlaEstoque && 
        Number(insumo?.quantidadeEstoque || 0) <= Number(insumo?.estoqueMinimo || 0)
      )

      if (insumosEstoqueBaixo.length > 0) {
        newAlerts.push({
          id: 'estoque-baixo',
          type: 'warning',
          title: 'Estoque Baixo',
          message: `${insumosEstoqueBaixo.length} insumo(s) com estoque abaixo do mínimo`,
          priority: 'medium',
          module: 'Insumos',
          createdAt: new Date().toISOString()
        })
      }
    }

    // Alertas de compras com validação
    if (compras && Array.isArray(compras)) {
      const comprasPendentes = compras.filter(compra => compra?.status === 'pendente')
      if (comprasPendentes.length > 5) {
        newAlerts.push({
          id: 'compras-pendentes',
          type: 'info',
          title: 'Compras Pendentes',
          message: `${comprasPendentes.length} compras aguardando aprovação`,
          priority: 'low',
          module: 'Compras',
          createdAt: new Date().toISOString()
        })
      }
    }

    setAlerts(newAlerts)
  }

  useEffect(() => {
    const resumo = calculateAtividadesResumo()
    setAtividadesResumo(resumo)
  }, [atividades])

  useEffect(() => {
    generateAlerts()
  }, [financeiroMetrics, obras, insumos, compras, atividadesResumo])

  const kpis = calculateKPIs()

  const getAlertIcon = (type: Alert['type']) => {
    switch (type) {
      case 'error': return <AlertTriangle className="w-5 h-5 text-red-500" />
      case 'warning': return <Clock className="w-5 h-5 text-yellow-500" />
      case 'info': return <Activity className="w-5 h-5 text-blue-500" />
      case 'success': return <CheckCircle className="w-5 h-5 text-green-500" />
    }
  }

  const getAlertBorderColor = (type: Alert['type']) => {
    switch (type) {
      case 'error': return 'border-l-red-500'
      case 'warning': return 'border-l-yellow-500'
      case 'info': return 'border-l-blue-500'
      case 'success': return 'border-l-green-500'
    }
  }

  if (leadsLoading || financeiroLoading || obrasLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard Geral</h1>
          <p className="text-gray-600 mt-1">Visão geral do sistema - {new Date().toLocaleDateString('pt-BR')}</p>
        </div>
        <div className="flex items-center space-x-4">
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value as '7d' | '30d' | '90d')}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="7d">Últimos 7 dias</option>
            <option value="30d">Últimos 30 dias</option>
            <option value="90d">Últimos 90 dias</option>
          </select>
        </div>
      </div>

      {/* KPIs Principais */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {kpis.map((kpi, index) => (
          <div key={index} className="bg-white rounded-lg shadow-md p-6 border-l-4 border-l-blue-500">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-600">{kpi.title}</p>
                <p className="text-2xl font-bold text-gray-900 mt-1">{kpi.value}</p>
                {kpi.subtitle && (
                  <p className="text-xs text-gray-500 mt-1">{kpi.subtitle}</p>
                )}
              </div>
              <div className={`p-3 rounded-full ${kpi.color} text-white`}>
                {kpi.icon}
              </div>
            </div>
            <div className="flex items-center mt-4">
              {kpi.changeType === 'increase' ? (
                <ArrowUp className="w-4 h-4 text-green-500 mr-1" />
              ) : (
                <ArrowDown className="w-4 h-4 text-red-500 mr-1" />
              )}
              <span className={`text-sm font-medium ${
                kpi.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
              }`}>
                {Math.abs(kpi.change).toFixed(1)}%
              </span>
            </div>
          </div>
        ))}
      </div>

      {/* 🔥 NOVA SEÇÃO: Resumo das Atividades */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Resumo das Atividades</h3>
          <Activity className="w-6 h-6 text-blue-500" />
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
          <div className="text-center p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-center mb-2">
              <FileText className="w-8 h-8 text-gray-600" />
            </div>
            <p className="text-2xl font-bold text-gray-900">{atividadesResumo.totalAtividades}</p>
            <p className="text-sm text-gray-600">Total de Atividades</p>
          </div>
          
          <div className="text-center p-4 bg-yellow-50 rounded-lg">
            <div className="flex items-center justify-center mb-2">
              <PauseCircle className="w-8 h-8 text-yellow-600" />
            </div>
            <p className="text-2xl font-bold text-yellow-700">{atividadesResumo.pendentes}</p>
            <p className="text-sm text-yellow-600">Pendentes</p>
          </div>
          
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <div className="flex items-center justify-center mb-2">
              <PlayCircle className="w-8 h-8 text-blue-600" />
            </div>
            <p className="text-2xl font-bold text-blue-700">{atividadesResumo.emAndamento}</p>
            <p className="text-sm text-blue-600">Em Andamento</p>
          </div>
          
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <div className="flex items-center justify-center mb-2">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <p className="text-2xl font-bold text-green-700">{atividadesResumo.concluidas}</p>
            <p className="text-sm text-green-600">Concluídas</p>
          </div>
          
          <div className="text-center p-4 bg-red-50 rounded-lg">
            <div className="flex items-center justify-center mb-2">
              <AlertCircle className="w-8 h-8 text-red-600" />
            </div>
            <p className="text-2xl font-bold text-red-700">{atividadesResumo.atrasadas}</p>
            <p className="text-sm text-red-600">Atrasadas</p>
          </div>
        </div>
        
        {/* Progresso Médio das Atividades */}
        <div className="mt-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Progresso Médio das Atividades</span>
            <span className="text-sm font-bold text-gray-900">{atividadesResumo.progressoMedio.toFixed(1)}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div 
              className="bg-blue-500 h-3 rounded-full transition-all duration-300" 
              style={{ width: `${Math.min(atividadesResumo.progressoMedio, 100)}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* Resumo dos Módulos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {/* CRM - Leads */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">CRM - Leads</h3>
            <Users className="w-6 h-6 text-blue-500" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total de Leads</span>
              <span className="font-semibold">{Number(leadsMetrics?.totalLeads) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Leads Fechados</span>
              <span className="font-semibold text-green-600">{Number(leadsMetrics?.leadsFechados) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Em Negociação</span>
              <span className="font-semibold text-yellow-600">
                {formatCurrency(leadsMetrics?.valorEmNegociacao)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Valor Fechado</span>
              <span className="font-semibold text-green-600">
                {formatCurrency(leadsMetrics?.valorTotalFechado)}
              </span>
            </div>
          </div>
        </div>

        {/* Projetos Ativos */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Projetos Ativos</h3>
            <Building2 className="w-6 h-6 text-orange-500" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total de Obras</span>
              <span className="font-semibold">{Number(obrasMetrics?.totalObras) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Em Andamento</span>
              <span className="font-semibold text-blue-600">{Number(obrasMetrics?.obrasAndamento) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Concluídas</span>
              <span className="font-semibold text-green-600">{Number(obrasMetrics?.obrasConcluidas) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Progresso Médio</span>
              <span className="font-semibold">{(Number(obrasMetrics?.progressoMedio) || 0).toFixed(1)}%</span>
            </div>
          </div>
        </div>

        {/* Orçamentos */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Orçamentos</h3>
            <FileText className="w-6 h-6 text-purple-500" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total</span>
              <span className="font-semibold">{Number(orcamentosMetrics?.totalOrcamentos) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Aprovados</span>
              <span className="font-semibold text-green-600">{Number(orcamentosMetrics?.orcamentosAprovados) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Em Análise</span>
              <span className="font-semibold text-yellow-600">{Number(orcamentosMetrics?.orcamentosAnalise) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Valor Total</span>
              <span className="font-semibold">
                {formatCurrency(orcamentosMetrics?.valorTotalOrcamentos)}
              </span>
            </div>
          </div>
        </div>

        {/* Contratos */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Contratos</h3>
            <Briefcase className="w-6 h-6 text-indigo-500" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total</span>
              <span className="font-semibold">{Number(contratosMetrics?.totalContratos) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Aprovados</span>
              <span className="font-semibold text-green-600">{Number(contratosMetrics?.contratosAprovados) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Em Análise</span>
              <span className="font-semibold text-yellow-600">{Number(contratosMetrics?.contratosAnalise) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Valor Total</span>
              <span className="font-semibold">
                {formatCurrency(contratosMetrics?.valorTotalContratos)}
              </span>
            </div>
          </div>
        </div>

        {/* Funcionários */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Funcionários</h3>
            <UserCheck className="w-6 h-6 text-green-500" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Ativos</span>
              <span className="font-semibold">{Number(funcionariosMetrics?.totalAtivos) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Alocados</span>
              <span className="font-semibold text-blue-600">{Number(funcionariosMetrics?.funcionariosAlocados) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Disponíveis</span>
              <span className="font-semibold text-green-600">{Number(funcionariosMetrics?.funcionariosDisponiveis) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Custo Mensal</span>
              <span className="font-semibold">
                {formatCurrency(funcionariosMetrics?.custoTotalMensal)}
              </span>
            </div>
          </div>
        </div>

        {/* Compras */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Compras</h3>
            <ShoppingCart className="w-6 h-6 text-red-500" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total do Mês</span>
              <span className="font-semibold">{Number(comprasMetrics?.totalComprasMes) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Pendentes</span>
              <span className="font-semibold text-yellow-600">{Number(comprasMetrics?.comprasPendentes) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Aprovadas</span>
              <span className="font-semibold text-green-600">{Number(comprasMetrics?.comprasAprovadas) || 0}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Valor Total</span>
              <span className="font-semibold">
                {formatCurrency(comprasMetrics?.valorTotalCompras)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Alertas e Notificações */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Alertas e Notificações</h3>
          <AlertTriangle className="w-6 h-6 text-red-500" />
        </div>
        
        {alerts.length === 0 ? (
          <div className="text-center py-8">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <p className="text-gray-500">Nenhum alerta no momento</p>
            <p className="text-sm text-gray-400">Todos os sistemas funcionando normalmente</p>
          </div>
        ) : (
          <div className="space-y-3">
            {alerts.slice(0, 5).map((alert) => (
              <div 
                key={alert.id} 
                className={`p-4 border-l-4 ${getAlertBorderColor(alert.type)} bg-gray-50 rounded-r-lg`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3">
                    {getAlertIcon(alert.type)}
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900">{alert.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">{alert.message}</p>
                      <div className="flex items-center mt-2 space-x-4">
                        <span className="text-xs text-gray-500">Módulo: {alert.module}</span>
                        <span className="text-xs text-gray-500">
                          {new Date(alert.createdAt).toLocaleString('pt-BR')}
                        </span>
                      </div>
                    </div>
                  </div>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    alert.priority === 'high' ? 'bg-red-100 text-red-800' :
                    alert.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {alert.priority === 'high' ? 'Alta' : 
                     alert.priority === 'medium' ? 'Média' : 'Baixa'}
                  </span>
                </div>
              </div>
            ))}
            
            {alerts.length > 5 && (
              <div className="text-center pt-4">
                <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                  Ver todos os alertas ({alerts.length})
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Resumo Financeiro */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Contas a Receber</h3>
            <TrendingUp className="w-6 h-6 text-green-500" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Pendente</span>
              <span className="font-semibold text-green-600">
                {formatCurrency(financeiroMetrics?.contasReceber)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-500 h-2 rounded-full" 
                style={{ 
                  width: `${Math.min(
                    (Number(financeiroMetrics?.contasReceber) || 0) / 
                    Math.max((Number(financeiroMetrics?.contasReceber) || 0) + (Number(financeiroMetrics?.contasPagar) || 0), 1) * 100, 
                    100
                  )}%` 
                }}
              ></div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Contas a Pagar</h3>
            <TrendingDown className="w-6 h-6 text-red-500" />
          </div>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Pendente</span>
              <span className="font-semibold text-red-600">
                {formatCurrency(financeiroMetrics?.contasPagar)}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-red-500 h-2 rounded-full" 
                style={{ 
                  width: `${Math.min(
                    (Number(financeiroMetrics?.contasPagar) || 0) / 
                    Math.max((Number(financeiroMetrics?.contasReceber) || 0) + (Number(financeiroMetrics?.contasPagar) || 0), 1) * 100, 
                    100
                  )}%` 
                }}
              ></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default DashboardGeral
